import {
  createContext,
  type MouseEvent,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useAnalytics } from '@/contexts/analytics'

type ToggleSKUCallback = (value: boolean) => void

type SKUListShortcutContext = {
  subscribeToBatchExpand: (
    productId: number,
    callback: ToggleSKUCallback
  ) => void
  unsubscribeToBatchExpand: (productId: number) => void
  isShortcutHintActive: boolean
  onClickToExpandSKUItem: (
    evt: MouseEvent<HTMLButtonElement>,
    productId: number,
    currentIsExpandedFromSKUItem: boolean
  ) => void
}

const SKUListShortcut = createContext<SKUListShortcutContext | null>(null)

export const SKUListShortcutProvider = ({ children }: PropsWithChildren) => {
  const [isShortcutHintActive, setIsShortcutHintActive] = useState(false)
  const [countClicksToExpand, setCountClicksToExpand] = useState(0)
  const toggleExpandedSKUCallbacks = useRef<Map<number, ToggleSKUCallback>>(
    new Map()
  )

  const analytics = useAnalytics()

  const onClickToExpandSKUItem = useCallback(
    (
      evt: MouseEvent<HTMLButtonElement>,
      productId: number,
      currentIsExpandedFromSKUItem: boolean
    ) => {
      const newExpandedValue = !currentIsExpandedFromSKUItem

      if (evt.shiftKey) {
        toggleAllExpanded(newExpandedValue)
        analytics.track('Clicked on the list', {
          Action: 'Expand/Collapse SKUs: Shortcut',
        })
        return
      }

      analytics.track('Clicked on the list', {
        Action: 'Expand/Collapse SKUs: Click',
      })
      toggleExpandedSKUCallbacks.current.get(productId)?.(newExpandedValue)
      setCountClicksToExpand((prevValue) => prevValue + 1)
    },
    [analytics]
  )

  const toggleAllExpanded = (newExpandedValue: boolean) => {
    for (const callback of toggleExpandedSKUCallbacks.current.values()) {
      callback(newExpandedValue)
    }
  }

  const subscribeToBatchExpand = (
    productId: number,
    callback: ToggleSKUCallback
  ) => toggleExpandedSKUCallbacks.current.set(productId, callback)

  const unsubscribeToBatchExpand = (productId: number) =>
    toggleExpandedSKUCallbacks.current.delete(productId)

  useEffect(() => {
    if (countClicksToExpand >= 3 && !isShortcutHintActive) {
      setIsShortcutHintActive(true)
    }
  }, [countClicksToExpand, isShortcutHintActive])

  const value = useMemo(
    () => ({
      subscribeToBatchExpand,
      unsubscribeToBatchExpand,
      isShortcutHintActive,
      onClickToExpandSKUItem,
    }),
    [isShortcutHintActive, onClickToExpandSKUItem]
  )

  return (
    <SKUListShortcut.Provider value={value}>
      {children}
    </SKUListShortcut.Provider>
  )
}

export const useSKUListShortcut = () => {
  const context = useContext(SKUListShortcut)

  if (!context) {
    throw new Error(
      'useSKUListShortcut must be used within a SKUListShortcutProvider'
    )
  }

  return context
}
